import React, { useContext, useEffect, useState } from 'react';
import { Helmet as MetaTags } from 'react-helmet';
import { FORM_ERROR } from 'final-form';
import { Form, EmailField, PasswordField } from '~/components/organism/Forms';
import { Auth } from '~/amplify';
import {
  navigate,
  RouteComponentProps,
  useLocation,
  WindowLocation,
} from '@gatsbyjs/reach-router';
import { AuthContext } from '~/Root/Auth';
import FormUtils from '~/components/organism/FormUtils';
import Button from '~/components/atom/Button';
import { Link } from '~/components/molecule/Link';
import TEST_ID from './index.testid';
import Catalog from '~/Catalog';
import { fireTrackingEvent } from '~/hooks/useFireTrackingEvent';
import { TAPFILLIATE_REF, setLocalStorageItem } from '~/util/localStorageKeys';
import validate from './utils/validate';
import getEmailAndPasswordFromMagicLink from './utils/getEmailAndPasswordFromMagicLink';
import { Body, Heading1 } from '~/components/atom/Typography';
import useStoreUtmParams from '~/hooks/useStoreUtmParams';
import JustificationContainer from '~/components/atom/JustificationContainer';
import AuthFormWrapper from '../components/AuthFormWrapper';
import { TEMP_PASSWORD_EXPIRED_MESSAGE } from './constants';
import Input from '~/components/molecule/Input';
import triggerResetTempPassword from '../utils/triggerResetTempPassword';
import UnauthNav from '~/components/organism/UnauthNav';
import createPageTitle from '~/util/createPageTitle';
import { reporter } from '~/hooks/useErrorReporter';

type Props = RouteComponentProps<{
  location: {
    state: {
      from?: string;
      email?: string;
      message?: string;
    };
  };
}>;

const text = {
  title: 'Inloggen',
  userNotConfirmed: 'Dit e-mailadres is nog niet geverifiëerd.',
  emailFieldLabel: Catalog.emailLabel,
  passwordFieldLabel: 'Wachtwoord',
  forgotPassword: 'Wachtwoord vergeten?',
  loginButton: 'Inloggen',
  noAccount: 'Nog geen account?',
  registerHere: 'Registreer je hier',
  loginFailMessage:
    'De combinatie van e-mailadres en wachtwoord is niet geldig.',
  unknownLoginError: Catalog.genericUnknownErrorMessage,
  successMessage:
    'Je e-mailadres is succesvol geverifiëerd. Je kan nu inloggen.',
};

export type Credentials = {
  email: string | null;
  password: string | null;
};

export type WindowState = {
  from?: string;
  email?: string;
  message?: string;
};

const Login: React.FCC<Props> = () => {
  useStoreUtmParams();
  const location = useLocation() as WindowLocation<WindowState>;
  const { ensureLogin } = useContext(AuthContext);
  const [infoMsg, setInfoMsg] = useState(location.state?.message || '');
  const [loading, setLoading] = useState(false);
  const [initialCredentials, setInitialCredentials] = useState<Credentials>({
    email: location.state?.email ?? null,
    password: null,
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tapfillateRef = params.get('ref');
    if (tapfillateRef) {
      setLocalStorageItem(
        TAPFILLIATE_REF,
        JSON.stringify({ date: Date.now(), ref: tapfillateRef }),
      );
    }

    // email just got verified and we landed on login again. Send event
    if (location.state?.message === text.successMessage) {
      fireTrackingEvent({
        event: 'register',
        message: location.state?.message,
      });
    }

    const credentials = getEmailAndPasswordFromMagicLink(location);
    if (credentials.email) proceedMagicLinkLogin(credentials);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFormSubmit = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    const loweredEmail = email.toLowerCase();
    setLoading(true);

    try {
      const signInResult = await Auth.signIn({
        username: loweredEmail,
        password,
      });

      if (!signInResult.isSignedIn) {
        // Handle different sign-in states
        switch (signInResult.nextStep.signInStep) {
          // This means this user has been invited. Setup their user details.
          case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED': {
            try {
              const userAttributes = await Auth.fetchUserAttributes();

              if (userAttributes && userAttributes.name) {
                return navigate('/setup-user-details', {
                  state: {
                    email,
                    name: userAttributes?.name,
                    phone: userAttributes?.['custom:phone'],
                  },
                });
              }

              return navigate('/setup-user-details', {
                state: { email },
              });
            } catch (attributeError) {
              reporter.captureException(
                new Error(`Failed to get user attributes: ${attributeError}`),
                'error',
              );
              return navigate('/setup-user-details', {
                state: { email },
              });
            }
          }

          case 'CONFIRM_SIGN_UP': {
            void navigate('/verify', { state: { email } });
            return {
              [FORM_ERROR]: text.userNotConfirmed,
            };
          }

          default:
            reporter.captureMessage(
              `Unhandled sign-in step: ${signInResult.nextStep.signInStep}`,
              'warning',
            );
            throw new Error('Unhandled authentication step');
        }
      } else {
        // Sign-in successful
        try {
          // For successful sign-in, just call ensureLogin to trigger the auth provider
          // to re-validate and set the user state
          ensureLogin();

          // When the submit button gets clicked and there is no password challenge
          // we should fire an event to GTM
          fireTrackingEvent({
            event: 'login',
            loginMethod: 'email',
          });

          return;
        } catch (getUserError) {
          reporter.captureMessage(
            `Failed to get current user after successful sign-in: ${getUserError}`,
            'error',
          );
          throw new Error('Failed to get user information');
        }
      }
    } catch (err: any) {
      // Handle AuthUserPoolException first
      if (
        err.name === 'AuthUserPoolException' ||
        err.__type === 'AuthUserPoolException' ||
        err.code === 'AuthUserPoolException'
      ) {
        reporter.captureException(err, 'error', {
          errorInfo: {
            errorType: 'AuthUserPoolException',
            message: err.message,
          },
        });
        return {
          [FORM_ERROR]:
            'Er is een probleem met de authenticatie configuratie. Neem contact op met de beheerder.',
        };
      }

      if (
        (err.name === 'NotAuthorizedException' ||
          err.__type === 'NotAuthorizedException') &&
        err.message === TEMP_PASSWORD_EXPIRED_MESSAGE
      ) {
        const result = await triggerResetTempPassword(loweredEmail);
        return {
          [FORM_ERROR]: result,
        };
      }

      if (
        err.name === 'UserNotConfirmedException' ||
        err.__type === 'UserNotConfirmedException'
      ) {
        void navigate('/verify', { state: { email } });
        return {
          [FORM_ERROR]: text.userNotConfirmed,
        };
      } else {
        return authErrorMsg(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const autoSubmitForm = () => {
    setTimeout(() => {
      if (document != null && document.getElementById !== null) {
        const element = document.getElementById('signin-form');

        if (element != null) {
          element.dispatchEvent(new Event('submit', { cancelable: true }));
        }
      }
    }, 0);
  };

  const proceedMagicLinkLogin = ({ email, password }: Credentials) => {
    setInitialCredentials(() => ({
      email,
      password: password ?? null,
    }));

    if (password) {
      autoSubmitForm();
    }
  };

  const login = () => {
    setInfoMsg('');
  };

  return (
    <>
      <MetaTags>
        <title>{createPageTitle(text.title)}</title>
        <meta
          name="description"
          content={`Inloggen op het portaal van ${Catalog.companyName}`}
        />
      </MetaTags>
      <UnauthNav
        buttons={[
          {
            label: 'Registeren',
            onClick: () => navigate('/register'),
            appearance: 'primary',
            size: 'large',
          },
        ]}
      />
      <AuthFormWrapper>
        <Heading1
          lineHeight="base"
          margin={['l', null]}
          color={{ group: 'accent' }}
          size="xxxxl"
          skewed
        >
          {text.title}
        </Heading1>

        <Form
          initialValues={initialCredentials}
          onSubmit={onFormSubmit}
          validate={validate}
        >
          {({ handleSubmit, submitError, submitting, pristine }) => {
            const submitDisabled =
              (pristine && // make it available for automagical link submit ->
                !initialCredentials.email &&
                !initialCredentials.password) ||
              submitting;

            return (
              <form
                id="signin-form"
                onSubmit={handleSubmit}
                data-testid="sign-in-form"
              >
                {submitError ? (
                  <Body
                    margin={[null]}
                    color={{ group: 'danger', variant: 'light' }}
                    size="base"
                    dataTestId={TEST_ID.SIGN_IN_ERROR_MESSAGE}
                  >
                    {submitError}
                  </Body>
                ) : null}

                {infoMsg && (
                  <Body
                    margin={[null]}
                    color={{ group: 'info' }}
                    size="base"
                    data-testid={TEST_ID.INFO_MESSAGE}
                  >
                    {infoMsg}
                  </Body>
                )}

                <JustificationContainer align="center" margin={['m', null]}>
                  <EmailField name="email">
                    {({ input, meta: { error, touched } }) => (
                      <Input
                        {...input}
                        size="large"
                        width="100%"
                        label={{
                          text: text.emailFieldLabel,
                          color: { group: 'white' },
                        }}
                        externalErrors={
                          error && touched
                            ? [FormUtils.showError(error, touched)]
                            : undefined
                        }
                        disabled={submitting}
                        autoFocus
                        autoComplete="username"
                      />
                    )}
                  </EmailField>
                </JustificationContainer>
                <JustificationContainer align="center" margin={['m', null]}>
                  <PasswordField name="password">
                    {({ input, meta: { error, touched } }) => (
                      <Input
                        {...input}
                        size="large"
                        width="100%"
                        label={{
                          text: text.passwordFieldLabel,
                          color: { group: 'white' },
                        }}
                        type="password"
                        autoComplete="current-password"
                        externalErrors={
                          error && touched
                            ? [FormUtils.showError(error, touched)]
                            : undefined
                        }
                        disabled={submitting}
                      />
                    )}
                  </PasswordField>
                </JustificationContainer>
                <JustificationContainer
                  align="center"
                  justification="space-between"
                  gap="base"
                >
                  <Link
                    to={'/forgot-password'}
                    dataTestId={TEST_ID.FORGOT_PASSWORD_LINK}
                  >
                    {text.forgotPassword}
                  </Link>
                  <Button
                    onClick={login}
                    appearance="primary"
                    size="medium"
                    type="submit"
                    disabled={submitDisabled || submitting}
                    dataTestId="login-button"
                    loading={submitting || loading}
                    label={text.loginButton}
                  />
                </JustificationContainer>

                <Body
                  align="center"
                  size="base"
                  color={{ group: 'white' }}
                  margin={['xl', null, null, null]}
                >
                  {text.noAccount}{' '}
                  <Link
                    to={`/register/${location.search}`}
                    dataTestId="register-link"
                  >
                    {text.registerHere}
                  </Link>
                </Body>
              </form>
            );
          }}
        </Form>
      </AuthFormWrapper>
    </>
  );
};

export default Login;

const authErrorMsg = (err: {
  __type: string;
  code: string;
  name: string;
  message: string;
}) => {
  // Handle AuthUserPoolException (new in v6)
  if (
    err.name === 'AuthUserPoolException' ||
    err.__type === 'AuthUserPoolException' ||
    err.code === 'AuthUserPoolException'
  ) {
    // This usually indicates a configuration issue
    reporter.captureException(err, 'error', {
      errorInfo: {
        errorType: 'AuthUserPoolException',
        message: err.message,
      },
    });
    return {
      [FORM_ERROR]:
        'Er is een probleem met de authenticatie configuratie. Neem contact op met de beheerder.',
    };
  }

  if (
    err.__type === 'UserNotFoundException' ||
    err.__type === 'NotAuthorizedException' ||
    err.code === 'UserNotFoundException' ||
    err.code === 'NotAuthorizedException' ||
    err.name === 'UserNotFoundException' ||
    err.name === 'NotAuthorizedException'
  ) {
    return { [FORM_ERROR]: text.loginFailMessage };
  }

  return {
    [FORM_ERROR]: text.unknownLoginError,
  };
};
